import { Component, OnInit, Input } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap';
import { ToasterService } from 'angular2-toaster';
import swal from 'sweetalert2';
import { AddEditPovConfigComponent } from './addEditPovConfig.component';
import { PovConfigService } from '../../../services/index';

const POV_STYLE_MAP: {[k: string]: {bg: string, color: string, border: string}} = {
    'Testing':      { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    'Trial':        { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    'Programing':   { bg: '#ede9fe', color: '#4c1d95', border: '#c4b5fd' },
    'Re-Programing':{ bg: '#ede9fe', color: '#4c1d95', border: '#c4b5fd' },
    'Re-Check up':  { bg: '#fef3c7', color: '#78350f', border: '#fcd34d' },
    'Re-Trial':     { bg: '#fce7f3', color: '#831843', border: '#f9a8d4' },
    'Fitting':      { bg: '#e0f2fe', color: '#0c4a6e', border: '#7dd3fc' },
    'OCT':          { bg: '#fef9c3', color: '#713f12', border: '#fde047' },
};

@Component({
    selector: 'pov-config-tab',
    templateUrl: './povConfig.component.html',
})
export class PovConfigComponent implements OnInit {
    @Input('tabTitle') title: string;
    @Input() active = false;
    @Input() isCloseable = false;
    @Input() dataContext;

    isLoadMask: Promise<any>;
    loadingIndicator = true;
    configData: any[]      = [];
    filteredData: any[]    = [];
    searchText             = '';
    bsModalRef: BsModalRef;
    popup: { visible: boolean, items: string[], top: number, left: number } = { visible: false, items: [], top: 0, left: 0 };

    constructor(
        private povConfigService: PovConfigService,
        private modalService: BsModalService,
        private toasterService: ToasterService
    ) {}

    ngOnInit(): void {
        this.getList();
    }

    getPovStyle(pov: string): {[k: string]: string} {
        const s = POV_STYLE_MAP[pov];
        return s
            ? { background: s.bg, color: s.color, border: '1.5px solid ' + s.border }
            : { background: '#f3f4f6', color: '#374151', border: '1.5px solid #d1d5db' };
    }

    splitPov(pov: string): string[] {
        return pov ? pov.split(',').map(p => p.trim()).filter(Boolean) : [];
    }

    applyFilter(): void {
        const txt = this.searchText.toLowerCase().trim();
        this.filteredData = txt
            ? this.configData.filter(r => (r.pov_name || '').toLowerCase().includes(txt))
            : this.configData.slice();
    }

    clearFilter(): void {
        this.searchText = '';
        this.filteredData = this.configData.slice();
    }

    getList(): void {
        this.isLoadMask = this.povConfigService.getList({
            start: '0', limit: '200', orderColumn: 'id', orderDir: 'asc', filter: ''
        }).then((res: any) => {
            this.loadingIndicator = false;
            this.configData   = res.data || [];
            this.filteredData = this.configData.slice();
        });
    }

    onView(row: any): void {
        let that = this;
        this.povConfigService.getByPov({ pov_name: row.pov_name }).then((res: any) => {
            that.bsModalRef = that.modalService.show(AddEditPovConfigComponent, {
                keyboard: false, ignoreBackdropClick: true, class: 'modal-lg'
            });
            let data = Object.assign({}, row);
            data['mode']                  = 'View';
            data['pov_name_arr']          = row.pov_name ? row.pov_name.split(',') : [];
            data['poc_ids_arr']           = (res && res.success && res.data) ? (res.data.poc_ids_arr || []).map(Number) : [];
            data['out_status_fol_ids_arr']= (res && res.success && res.data) ? (res.data.out_status_fol_ids_arr || []).map(Number) : [];
            setTimeout(function () {
                that.bsModalRef.content.title   = 'View POV Config — ' + (row.pov_name || '');
                that.bsModalRef.content.rowdata = data;
            }, 100);
        });
    }

    onAddEdit(mode: string, row: any): void {
        let that = this;
        this.bsModalRef = this.modalService.show(AddEditPovConfigComponent, {
            keyboard: false, ignoreBackdropClick: true, class: 'modal-lg'
        });
        let data = mode === 'Add' ? {} : Object.assign({}, row);
        data['mode'] = mode;
        if (mode === 'Edit') {
            that.povConfigService.getByPov({ pov_name: row.pov_name }).then((res: any) => {
                data['pov_name_arr']          = row.pov_name ? row.pov_name.split(',') : [];
                data['poc_ids_arr']           = (res && res.success && res.data) ? (res.data.poc_ids_arr || []).map(Number) : [];
                data['out_status_fol_ids_arr']= (res && res.success && res.data) ? (res.data.out_status_fol_ids_arr || []).map(Number) : [];
                setTimeout(function () {
                    that.bsModalRef.content.title   = 'Edit POV Config — ' + (row.pov_name || '');
                    that.bsModalRef.content.rowdata = data;
                    that.bsModalRef.content.saveEvent.subscribe(() => { that.getList(); });
                }, 100);
            });
        } else {
            setTimeout(function () {
                that.bsModalRef.content.title = 'Add POV Config';
                that.bsModalRef.content.rowdata = data;
                that.bsModalRef.content.saveEvent.subscribe(() => { that.getList(); });
            }, 100);
        }
    }

    showCountPopup(event: MouseEvent, items: string[]): void {
        event.stopPropagation();
        const el = event.target as HTMLElement;
        const rect = el.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.popup = { visible: true, items: items || [], top: rect.bottom + scrollTop + 4, left: rect.left };
    }

    closePopup(): void {
        this.popup.visible = false;
    }

    onDelete(row: any): void {
        let that = this;
        swal({ title: 'Confirm Delete', text: `Delete config for "${row.pov_name}"?`, type: 'warning', showCancelButton: true, confirmButtonText: 'Yes, Delete' })
            .then((result) => {
                if (result.value) {
                    that.povConfigService.delete({ id: row.id }).then((res: any) => {
                        if (res.success) { swal('Deleted', res.message, 'success'); that.getList(); }
                        else             { swal('Error',   res.message, 'error'); }
                    });
                }
            });
    }
}



// WEBPACK FOOTER //
// /Applications/MAMP/htdocs/crm-soundsgood/src/app/components/master/povConfig/povConfig.component.ts